import React, { useState, useEffect } from 'react'
import { Info, Warning, Check, X, CaretDown, CaretRight } from '@phosphor-icons/react'
import { Tooltip } from '@/components/ui/Tooltip'
import { toast } from 'sonner'
import {
  ALL_ENRICHMENT_BLOCKS,
  FALLBACK_ENRICHMENT_BLOCK,
  NO_BLOCKS_MESSAGE,
  offerableProviders,
} from '../../enrichmentConfig'
import { settingsApi } from '@/lib/api'
import type { WorkflowNodeData, EnrichmentBlock } from '@/types/workflow'
import { useSettingsStore } from '../../../../stores/settingsStore'

interface EnrichContactsConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import { cn } from '@/lib/utils'
import {
  inputClass,
  inputStyle,
  sectionClass,
  infoBannerClass,
  checkboxInputClass,
  checkboxLabelClass,
  sectionEyebrowClass,
  sectionEyebrowStyle,
} from './_kit'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'

const EXECUTION_MODE_OPTIONS = [
  // Plain-language labels (AB#21977): "Parallel"/"Sequential" describe how
  // the code runs, not what the user is choosing between.
  { value: 'parallel', label: 'Fast — check all sources at once' },
  { value: 'sequential', label: 'Gentle — check sources one at a time' },
]

// The one list, shared with the node card so the two can never quote
// different totals. See enrichmentConfig.ts.
const ALL_BLOCKS = ALL_ENRICHMENT_BLOCKS as readonly string[] as EnrichmentBlock[]

interface BlockInfo {
  key: EnrichmentBlock
  label: string
  description: string
  group: 'research' | 'analysis' | 'database' | 'custom'
  requiresLLM: boolean
}

const BLOCK_INFO: BlockInfo[] = [
  { key: 'person', label: 'Person', description: 'Bio, roles, skills, social presence', group: 'research', requiresLLM: true },
  { key: 'company', label: 'Company', description: 'Funding, size, tech stack, news', group: 'research', requiresLLM: true },
  { key: 'sector', label: 'Sector', description: 'Market size, trends, competitors', group: 'research', requiresLLM: true },
  { key: 'economy', label: 'Economy', description: 'GDP growth, business climate, outlook', group: 'research', requiresLLM: true },
  { key: 'outreach_hooks', label: 'Outreach Hooks', description: 'News hooks, role changes, icebreakers', group: 'analysis', requiresLLM: true },
  { key: 'buying_signals', label: 'Buying Signals', description: 'Pain points, budget cycle, authority', group: 'analysis', requiresLLM: true },
  { key: 'communication_prefs', label: 'Communication Prefs', description: 'Tone, best time, content preferences', group: 'analysis', requiresLLM: true },
  { key: 'competitive_intel', label: 'Competitive Intel', description: 'Current tools, competitor weaknesses', group: 'analysis', requiresLLM: true },
  { key: 'content_relevance', label: 'Content Relevance', description: 'Engaged topics, speaking events', group: 'analysis', requiresLLM: true },
  { key: 'relationship_map', label: 'Relationship Map', description: 'Colleagues at same company (DB only)', group: 'database', requiresLLM: false },
  { key: 'custom_ai', label: 'Custom AI', description: 'Run your custom prompt with variable injection', group: 'custom', requiresLLM: true },
]

const GROUP_LABELS: Record<string, string> = {
  research: 'Research',
  analysis: 'Sales Intelligence',
  database: 'Database',
  custom: 'Custom',
}

interface ProviderInfo {
  name: string
  label: string
  available: boolean
  config_schema?: Record<string, any>
}

const EnrichContactsConfig: React.FC<EnrichContactsConfigProps> = ({ data, updateField }) => {
  const { recipes, fetchRecipes } = useSettingsStore()
  const [hasOpenAI, setHasOpenAI] = useState<boolean | null>(null)
  const [hasAnthropic, setHasAnthropic] = useState<boolean | null>(null)
  const [providers, setProviders] = useState<ProviderInfo[]>([])
  const [providersLoading, setProvidersLoading] = useState(true)
  const [expandedProviders, setExpandedProviders] = useState<Record<string, boolean>>({})

  const config = data.enrich_contacts_config || {
    skip_already_enriched: true,
    max_age_days: 0,
    enabled_blocks: [...ALL_BLOCKS],
    providers: [{ name: 'native', enabled: true, config: {} }],
    execution_mode: 'parallel',
  }

  // Backfill enabled_blocks for existing configs that don't have it
  if (!config.enabled_blocks) {
    config.enabled_blocks = [...ALL_BLOCKS]
  }

  // Backfill providers array. `undefined` here means the config predates the
  // `providers` field entirely (a brand-new node, or a workflow saved before
  // multi-provider support existed) — default it to Native (AI) enabled so
  // the UI matches the backend's backward-compat fallback (workflow_enrichment.py
  // treats a missing/falsy `providers` as "run native"). An *explicit* `[]`
  // means the user deliberately unchecked every provider — leave it alone.
  if (config.providers === undefined) {
    config.providers = [{ name: 'native', enabled: true, config: {} }]
  }

  const updateConfig = (key: string, value: any) => {
    updateField('enrich_contacts_config', { ...config, [key]: value })
  }

  // Fetch enrichment providers
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const { integrationsApi } = await import('@/lib/api')
        const res = await integrationsApi.getEnrichmentProviders()
        // Filtered client-side too: the frontend and backend deploy
        // separately, so this screen can run against a backend that still
        // returns the parked vendors. See PARKED_ENRICHMENT_PROVIDERS.
        setProviders(offerableProviders(res.data.providers))
      } catch {
        setProviders([{ name: 'native', label: 'Native (AI)', available: true, config_schema: {} }])
      } finally {
        setProvidersLoading(false)
      }
    }
    fetchProviders()
  }, [])

  // Fetch custom AI recipes
  useEffect(() => {
    fetchRecipes().catch(() => {})
  }, [fetchRecipes])

  // Check which API keys are configured
  useEffect(() => {
    settingsApi.getConfig()
      .then((res) => {
        const cfg = res.data
        setHasOpenAI(!!cfg?.openai_api_key_set)
        setHasAnthropic(!!cfg?.anthropic_api_key_set)
      })
      .catch(() => {
        setHasOpenAI(false)
        setHasAnthropic(false)
      })
  }, [])

  // Whether the ACCOUNT has an LLM key of its own. Used ONLY to tell the user
  // whose key is paying, never to disable anything -- see the note below.
  const usingPlatformKey = hasOpenAI === false && hasAnthropic === false

  // No hasAnyLLMKey gate any more. It was computed from the ACCOUNT's own
  // OpenAI/Anthropic key (auth.get_user_config reads config_user.*_api_key),
  // but Native runs on the platform key too -- get_available_providers marks it
  // available when EITHER is present. So on any deployment with a platform key,
  // which is the normal case and the basis of the whole our-key/their-key
  // pricing, an account with no personal key saw Native offered while 10 of its
  // 11 blocks were greyed out as "No API key". The blocks were wrong, not the
  // provider row: `provider.available` already carries the server's verdict and
  // still disables the provider itself when neither key exists.
  const isLoading = hasOpenAI === null

  const toggleBlock = (block: EnrichmentBlock) => {
    const current = config.enabled_blocks || ALL_BLOCKS
    if (current.includes(block)) {
      const remaining = current.filter((b: EnrichmentBlock) => b !== block)
      // Unticking the last one is the same dead end as "None": the graph skips
      // the LLM, returns empty blocks, and the run still costs its credits.
      if (remaining.length === 0) {
        toast.error(NO_BLOCKS_MESSAGE)
        return
      }
      updateConfig('enabled_blocks', remaining)
    } else {
      updateConfig('enabled_blocks', [...current, block])
    }
  }

  const selectAll = () => updateConfig('enabled_blocks', [...ALL_BLOCKS])
  // "None" has always kept relationship_map rather than clearing outright, it
  // just never said so, which read as the button being broken. Now it says so.
  const selectNone = () => {
    updateConfig('enabled_blocks', [FALLBACK_ENRICHMENT_BLOCK])
    toast.error(NO_BLOCKS_MESSAGE)
  }

  const isProviderEnabled = (providerName: string): boolean => {
    return (config.providers || []).some((p: any) =>
      typeof p === 'string' ? p === providerName : p.name === providerName
    )
  }

  const getProviderConfig = (providerName: string): Record<string, any> => {
    const entry = (config.providers || []).find((p: any) =>
      typeof p === 'object' && p.name === providerName
    )
    return entry?.config || {}
  }

  const toggleProvider = (providerName: string) => {
    const currentProviders = config.providers || []
    if (isProviderEnabled(providerName)) {
      const updated = currentProviders.filter((p: any) =>
        typeof p === 'string' ? p !== providerName : p.name !== providerName
      )
      updateField('enrich_contacts_config', { ...config, providers: updated })
    } else {
      const updated = [...currentProviders, { name: providerName, config: {} }]
      updateField('enrich_contacts_config', { ...config, providers: updated })
      setExpandedProviders(prev => ({ ...prev, [providerName]: true }))
    }
  }

  const updateProviderConfig = (providerName: string, providerConfig: Record<string, any>) => {
    const currentProviders = config.providers || []
    const updated = currentProviders.map((p: any) => {
      const name = typeof p === 'string' ? p : p.name
      if (name === providerName) {
        return { name: providerName, config: providerConfig }
      }
      return p
    })
    updateField('enrich_contacts_config', { ...config, providers: updated })
  }

  const toggleProviderExpanded = (providerName: string) => {
    setExpandedProviders(prev => ({ ...prev, [providerName]: !prev[providerName] }))
  }

  const groups = ['research', 'analysis', 'database', 'custom'] as const

  return (
    <div className={sectionClass}>
      {/* Label */}
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="e.g., Enrich before outreach"
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>

      {/* Source availability */}
      {!isLoading && (
        <Field label="Sources" as="group" className="space-y-1.5">
          <div className="flex gap-2">
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs ${
              hasOpenAI
                ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                : 'border-border/50 bg-muted text-muted-foreground'
            }`}>
              {hasOpenAI ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              OpenAI
            </div>
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs ${
              hasAnthropic
                ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                : 'border-border/50 bg-muted text-muted-foreground'
            }`}>
              {hasAnthropic ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              Anthropic
            </div>
          </div>
        </Field>
      )}

      {/* Info box */}
      <div className={infoBannerClass}>
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p className="text-xs">
          Select which enrichment blocks to run. Disabled blocks will be skipped.
          Analysis blocks depend on Research blocks for best results.
        </p>
      </div>

      {/* Enrichment Providers */}
      {providers.length > 0 && (
        <Field label="Enrichment Providers" as="group">
          <div className="space-y-2">
            {providers.map(provider => {
              const enabled = isProviderEnabled(provider.name)
              const expanded = expandedProviders[provider.name] || false
              return (
                <div key={provider.name} className={`rounded-xl border transition-all duration-150 ${
                  enabled
                    ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/10'
                    : 'border-border/50'
                }`}>
                  <div className="flex items-center gap-2 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => provider.available && toggleProvider(provider.name)}
                      disabled={!provider.available}
                      className={checkboxInputClass}
                    />
                    <button
                      type="button"
                      onClick={() => enabled && toggleProviderExpanded(provider.name)}
                      className="flex items-center gap-1 flex-1 text-left"
                      disabled={!enabled}
                    >
                      {enabled ? (
                        expanded ? <CaretDown className="w-3.5 h-3.5 text-muted-foreground" /> : <CaretRight className="w-3.5 h-3.5 text-muted-foreground" />
                      ) : (
                        <CaretRight className="w-3.5 h-3.5 text-muted-foreground/50" />
                      )}
                      <span className={`text-sm font-medium ${
                        provider.available
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                      }`}>
                        {provider.label}
                      </span>
                    </button>
                    {!provider.available && (
                      <span className="text-[10px] text-amber-500 font-medium">Configure in Settings</span>
                    )}
                    {/* Native on the platform key: say so and say what it costs.
                        Informational only -- everything stays selectable, which
                        is the difference between this and the "No API key" gate
                        it replaces. */}
                    {provider.name === 'native' && provider.available && usingPlatformKey && !isLoading && (
                      <Tooltip label="Using the platform's AI key, so this costs credits. Add your own in Settings > AI & Models to avoid them.">
                        <button
                          type="button"
                          tabIndex={0}
                          aria-label="Using the platform AI key"
                          className="inline-flex items-center text-amber-500 flex-shrink-0"
                        >
                          <Warning className="w-4 h-4" weight="fill" />
                        </button>
                      </Tooltip>
                    )}
                  </div>

                  {/* Native provider expanded config: show block checkboxes */}
                  {enabled && expanded && provider.name === 'native' && (
                    <div className="px-3 pb-3 border-t border-border/50 pt-2">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-muted-foreground">Enrichment Blocks</p>
                        <div className="flex gap-1">
                          <button type="button" onClick={selectAll} className="text-[10px] text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline transition-all duration-150">All</button>
                          <span className="text-[10px] text-border">|</span>
                          <button type="button" onClick={selectNone} className="text-[10px] text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline transition-all duration-150">None</button>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {groups.map(group => (
                          <div key={group}>
                            <p className={cn(sectionEyebrowClass, 'mb-1.5')} style={sectionEyebrowStyle}>{GROUP_LABELS[group]}</p>
                            <div className="space-y-1">
                              {BLOCK_INFO.filter(b => b.group === group).map(block => {
                                const blockEnabled = config.enabled_blocks.includes(block.key)
                                return (
                                  <div key={block.key}>
                                    <button
                                      type="button"
                                      onClick={() => toggleBlock(block.key)}
                                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl border transition-all duration-150 text-left ${
                                        blockEnabled
                                          ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/15'
                                          : 'border-border/50 hover:border-border'
                                      }`}
                                    >
                                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                        blockEnabled
                                          ? 'bg-[#5e6ad2] border-[#5e6ad2]'
                                          : 'border-border/60'
                                      }`}>
                                        {blockEnabled && <Check className="w-3 h-3 text-white" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-foreground">{block.label}</p>
                                        <p className="text-[10px] text-muted-foreground">{block.description}</p>
                                      </div>
                                    </button>
                                    {block.key === 'custom_ai' && config.enabled_blocks.includes('custom_ai') && (
                                      <div className="ml-6 mt-2 space-y-1">
                                        <div className="text-xs text-muted-foreground mb-1">Select recipes to run:</div>
                                        {recipes.map(recipe => (
                                          <label key={recipe.id} className={cn(checkboxLabelClass, 'text-sm')}>
                                            <input
                                              type="checkbox"
                                              checked={(config.selected_recipe_ids || []).includes(recipe.id)}
                                              onChange={() => {
                                                const current = config.selected_recipe_ids || []
                                                const updated = current.includes(recipe.id)
                                                  ? current.filter((id: number) => id !== recipe.id)
                                                  : [...current, recipe.id]
                                                updateConfig('selected_recipe_ids', updated)
                                              }}
                                              className={checkboxInputClass}
                                            />
                                            <span>{recipe.name}</span>
                                            <span className="text-xs text-muted-foreground">({recipe.model})</span>
                                          </label>
                                        ))}
                                        {recipes.length === 0 && (
                                          <div className="text-xs text-muted-foreground">No recipes created yet. Go to Settings &rarr; Custom AI Recipes.</div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-2">
                        {config.enabled_blocks.length} of {ALL_BLOCKS.length} blocks enabled
                      </p>
                    </div>
                  )}

                  {/* Data provider expanded config (PDL, Explorium, etc.) */}
                  {enabled && expanded && provider.name !== 'native' && provider.config_schema && (
                    <div className="px-3 pb-3 border-t border-border/50 pt-2 space-y-2">
                      <p className="text-xs font-medium text-muted-foreground mb-1">{provider.label} Options</p>
                      {Object.entries(provider.config_schema).map(([key, schema]: [string, any]) => {
                        if (schema.type === 'bool') {
                          return (
                            <label key={key} className={checkboxLabelClass}>
                              <input
                                type="checkbox"
                                checked={getProviderConfig(provider.name)[key] !== false}
                                onChange={(e) => updateProviderConfig(provider.name, {
                                  ...getProviderConfig(provider.name),
                                  [key]: e.target.checked,
                                })}
                                className={checkboxInputClass}
                              />
                              <span className="text-sm">
                                {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                              </span>
                            </label>
                          )
                        }
                        if (schema.type === 'field_group') {
                          const groupConfig = getProviderConfig(provider.name)[key] || {}
                          const fields = schema.fields || {}
                          const fieldKeys = Object.keys(fields)
                          const enabledCount = fieldKeys.filter(fk => groupConfig[fk] !== false).length
                          const allEnabled = enabledCount === fieldKeys.length
                          const noneEnabled = enabledCount === 0
                          return (
                            <div key={key} className="border border-border/40 rounded-lg overflow-hidden">
                              <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50">
                                <p className={sectionEyebrowClass} style={sectionEyebrowStyle}>
                                  {schema.label || key.replace(/_/g, ' ')}
                                </p>
                                <div className="flex gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const allOn: Record<string, boolean> = {}
                                      fieldKeys.forEach(fk => { allOn[fk] = true })
                                      updateProviderConfig(provider.name, {
                                        ...getProviderConfig(provider.name),
                                        [key]: allOn,
                                      })
                                    }}
                                    className={`text-[10px] transition-all duration-150 ${allEnabled ? 'text-[#5e6ad2] dark:text-[#a5b4fc] font-medium' : 'text-muted-foreground hover:text-[#5e6ad2] dark:hover:text-[#a5b4fc]'}`}
                                  >
                                    All
                                  </button>
                                  <span className="text-[10px] text-border">|</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const allOff: Record<string, boolean> = {}
                                      fieldKeys.forEach(fk => { allOff[fk] = false })
                                      updateProviderConfig(provider.name, {
                                        ...getProviderConfig(provider.name),
                                        [key]: allOff,
                                      })
                                    }}
                                    className={`text-[10px] transition-all duration-150 ${noneEnabled ? 'text-[#5e6ad2] dark:text-[#a5b4fc] font-medium' : 'text-muted-foreground hover:text-[#5e6ad2] dark:hover:text-[#a5b4fc]'}`}
                                  >
                                    None
                                  </button>
                                </div>
                              </div>
                              <div className="px-3 py-1.5 space-y-1">
                                {fieldKeys.map(fk => {
                                  const fieldSchema = fields[fk]
                                  const checked = groupConfig[fk] !== false
                                  return (
                                    <label key={fk} className={cn(checkboxLabelClass, 'py-0.5')}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          updateProviderConfig(provider.name, {
                                            ...getProviderConfig(provider.name),
                                            [key]: { ...groupConfig, [fk]: e.target.checked },
                                          })
                                        }}
                                        className={cn(checkboxInputClass, 'h-3.5 w-3.5')}
                                      />
                                      <span className="text-xs">
                                        {fieldSchema.label || fk.replace(/_/g, ' ')}
                                      </span>
                                    </label>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        }
                        return null
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </Field>
      )}

      {/* Loading state while providers are being fetched */}
      {providersLoading && providers.length === 0 && (
        <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
          <div className="w-4 h-4 border-2 border-[#5e6ad2] border-t-transparent rounded-full animate-spin" />
          Loading enrichment providers...
        </div>
      )}

      {/* Enrichment speed (was "Execution Mode" — AB#21977) */}
      <Field label="Enrichment speed">
        {(f) => (
          <Select
            {...f}
            value={config.execution_mode || 'parallel'}
            onChange={(value) => updateField('enrich_contacts_config', { ...config, execution_mode: value })}
            options={EXECUTION_MODE_OPTIONS}
            className="w-full"
          />
        )}
      </Field>
      <p className="text-xs text-muted-foreground -mt-2">
        Fast enriches quicker by checking all your data sources at the same time (recommended).
        Gentle is slower but easier on data-provider limits.
      </p>

      {/* Skip already enriched */}
      <label className={checkboxLabelClass}>
        <input
          type="checkbox"
          checked={config.skip_already_enriched}
          onChange={(e) => updateConfig('skip_already_enriched', e.target.checked)}
          className={checkboxInputClass}
        />
        <span className="text-sm">Skip already enriched contacts</span>
      </label>

      {/* Max age — removed as redundant (AB#21975). Backend
          (workflow_enrichment.py's max_age_days handling) is left intact;
          node_config.get("max_age_days", 0) simply defaults to 0 now that
          this field no longer writes to it, so "Skip already enriched
          contacts" behaves as a permanent skip once set.
      {config.skip_already_enriched && (
        <Field
          label="Re-enrich after (days)"
          hint="Set to 0 to never re-enrich. Otherwise, contacts enriched more than N days ago will be re-enriched."
        >
          {(f) => (
            <input
              {...f}
              type="number"
              min={0}
              value={config.max_age_days}
              onChange={(e) => updateConfig('max_age_days', parseInt(e.target.value) || 0)}
              placeholder="0"
              className={inputClass} style={inputStyle}
            />
          )}
        </Field>
      )} */}
    </div>
  )
}

export default EnrichContactsConfig
